import { Popup } from "@hatch-digital-nl/abs-storybook/legacy";
import {
    setFormValues,
    setIsEditEmployeeModalOpen,
    setIsPatchEditEmployeeSuccess,
    setShowEmailEdit,
    setNewEmailAddress,
    setEmailError,
    setResetEmailState,
    EditEmployeeModalInterface,
} from "./EditEmployeeModalSlice";
import { getResetEmailState } from "./utils/getResetEmailState";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useIsEstablishmentManager } from "../../common/utils/useIsEstablishmentManager";
import Skeleton from "react-loading-skeleton";
import { useGetFunctionGroupsQuery } from "../../common/api/functionGroup";
import { FormEvent, JSX, useEffect } from "react";
import {
    usePatchEmployeeMutation,
    useChangeEmailMutation,
    useCancelChangeEmailMutation,
    useResendChangeEmailMutation,
    useGetEmployeeMeQuery,
} from "../../common/api/employee";
import { SelectFieldComponent } from "./components/SelectFieldComponent";
import { InputFieldComponent } from "./components/InputFieldComponent";
import { SubmitButtonComponent } from "./components/SubmitButtonComponent";
import { setEmployeeFormValues } from "./utils/setEmployeeFormValues";
import { handleFormSubmit } from "./utils/handleFormSubmit";
import { NewEmailAddressComponent } from "./components/NewEmailAddressComponent";
import { PendingEmailChangeNoticeComponent } from "./components/PendingEmailChangeNoticeComponent";
import editField from "../../common/images/edit-white.svg";
import { getUserFullName } from "../../common/utils/getUserFullName";

export const EditEmployeeModalContainer = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const { data: functionGroupData, isLoading: functionGroupDataIsloading } =
        useGetFunctionGroupsQuery({ items_per_page: 999 });
    const {
        data: employeeMeData,
        refetch: refetchEmployeeMe,
        isFetching: employeeMeIsFetching,
    } = useGetEmployeeMeQuery({});
    const isEstablishmentManager = useIsEstablishmentManager();
    const {
        formValues,
        functionGroupInputValue,
        isEditEmployeeModalOpen,
        selectedEmployee,
        error,
        newEmailAddress,
        emailError,
        showEmailEdit,
    } = useAppSelector(
        (state) => state.editEmployeeModal,
    ) as EditEmployeeModalInterface;
    const [
        patchEditEmployee,
        {
            isLoading: isPatchEditEmployeeLoading,
            isSuccess: isPatchEditEmployeeSuccess,
        },
    ] = usePatchEmployeeMutation();

    const [changeEmail, { isLoading: changeEmailIsLoading }] =
        useChangeEmailMutation();
    const [cancelChangeEmail, { isLoading: cancelChangeEmailIsLoading }] =
        useCancelChangeEmailMutation();
    const [resendChangeEmail, { isLoading: resendChangeEmailIsLoading }] =
        useResendChangeEmailMutation();

    // Email state is now managed in Redux

    useEffect(() => {
        if (selectedEmployee && isEditEmployeeModalOpen) {
            setEmployeeFormValues(selectedEmployee, dispatch);
        }
    }, [isEditEmployeeModalOpen, selectedEmployee, dispatch]);

    useEffect(() => {
        if (!isPatchEditEmployeeSuccess) {
            dispatch(setIsPatchEditEmployeeSuccess(false));
        }
    }, [isPatchEditEmployeeSuccess, dispatch]);

    if (!formValues) {
        return null;
    }

    return (
        <Popup
            className={"c-edit-employee-modal"}
            title={"Medewerker bewerken"}
            widePopup={true}
            onCloseClick={() => {
                dispatch(setIsEditEmployeeModalOpen(false));
                dispatch(setFormValues(null));
            }}
        >
            <div className={"c-edit-employee-modal__header"}>
                <p>
                    {getUserFullName(selectedEmployee?.user)}{" "}
                    {selectedEmployee?.establishment?.name}
                </p>
            </div>
            {error && <p className={"c-error"}>{error}</p>}
            {emailError && <p className={"c-error"}>{emailError}</p>}
            {functionGroupDataIsloading ||
            cancelChangeEmailIsLoading ||
            resendChangeEmailIsLoading ||
            employeeMeIsFetching ? (
                <Skeleton
                    containerClassName={"c-edit-employee-modal__skeleton"}
                    count={6}
                    inline={true}
                    width={"49%"}
                    height={"5.5rem"}
                    borderRadius={"0.8rem"}
                />
            ) : (
                <form
                    className={"c-create-employee-modal__form"}
                    onSubmit={(event) =>
                        handleFormSubmit({
                            event,
                            emailError,
                            newEmail: newEmailAddress,
                            selectedEmployee,
                            dispatch,
                            changeEmail,
                            formValues,
                            patchEditEmployee,
                            refetchEmployeeMe,
                        })
                    }
                >
                    <div className={"c-create-employee-modal__form__row"}>
                        <InputFieldComponent
                            id={1}
                            value={formValues?.firstName}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        firstName: value,
                                    }),
                                )
                            }
                            type={"text"}
                            required={true}
                            title={"Voornaam"}
                            errorMessage={"Voornaam is verplicht"}
                        />
                        <InputFieldComponent
                            id={10}
                            value={formValues?.insertion}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        insertion: value,
                                    }),
                                )
                            }
                            type={"text"}
                            title={"Tussenvoegsel"}
                        />
                        <InputFieldComponent
                            id={11}
                            value={formValues?.lastName}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        lastName: value,
                                    }),
                                )
                            }
                            type={"text"}
                            required={true}
                            title={"Achternaam"}
                            errorMessage={"Achternaam is verplicht"}
                        />
                    </div>
                    <div className={"c-create-employee-modal__form__row"}>
                        <InputFieldComponent
                            id={12}
                            value={formValues?.placeOfBirth}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        placeOfBirth: value,
                                    }),
                                )
                            }
                            type={"text"}
                            required={false}
                            title={"Geboorteplaats"}
                        />
                    </div>

                    {isEstablishmentManager && (
                        <div className={"c-create-employee-modal__form__row"}>
                            <SelectFieldComponent
                                formValues={formValues}
                                functionGroupInputValue={
                                    functionGroupInputValue
                                }
                                functionGroupOptions={
                                    functionGroupData
                                        ? functionGroupData.data.map(
                                              (item) => ({
                                                  label: item.name,
                                                  value: item.id.toString(),
                                              }),
                                          )
                                        : []
                                }
                                dispatch={dispatch}
                            />
                        </div>
                    )}
                    <div className={"c-create-employee-modal__form__row"}>
                        <InputFieldComponent
                            id={2}
                            type={"text"}
                            required={true}
                            value={selectedEmployee?.establishment?.name}
                            title={"Vestiging"}
                            errorMessage={"Vestiging is verplicht"}
                        />
                        <InputFieldComponent
                            id={3}
                            type={"email"}
                            disabled={true}
                            required={true}
                            value={selectedEmployee?.user?.private_email}
                            title={"Privé e-mailadres"}
                            errorMessage={"Privé e-mailadres is verplicht"}
                            iconEdit={editField}
                            changeInputField={
                                !selectedEmployee?.pending_email_change &&
                                (!isEstablishmentManager ||
                                    employeeMeData?.id === selectedEmployee?.id)
                                    ? () => dispatch(setShowEmailEdit(true))
                                    : undefined
                            }
                            onClick={
                                !selectedEmployee?.pending_email_change &&
                                (!isEstablishmentManager ||
                                    employeeMeData?.id === selectedEmployee?.id)
                                    ? () => dispatch(setShowEmailEdit(true))
                                    : undefined
                            }
                        />
                    </div>
                    {showEmailEdit && (
                        <NewEmailAddressComponent
                            onEmailChange={(email) =>
                                dispatch(setNewEmailAddress(email))
                            }
                            onErrorChange={(errorMsg) =>
                                dispatch(setEmailError(errorMsg))
                            }
                            initialEmailError={emailError}
                            onClose={() => {
                                dispatch(
                                    setResetEmailState(getResetEmailState()),
                                );
                            }}
                        />
                    )}
                    {selectedEmployee?.pending_email_change && (
                        <PendingEmailChangeNoticeComponent
                            selectedEmployee={selectedEmployee}
                            dispatch={dispatch}
                            resendChangeEmail={resendChangeEmail}
                            cancelChangeEmail={cancelChangeEmail}
                            refetchEmployeeMe={refetchEmployeeMe}
                        />
                    )}
                    <div className={"c-create-employee-modal__form__row"}>
                        <InputFieldComponent
                            id={6}
                            type={"email"}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        businessEmail: value,
                                    }),
                                )
                            }
                            value={formValues?.businessEmail}
                            title={"Zakelijke e-mail"}
                            optional={true}
                        />
                        <InputFieldComponent
                            id={7}
                            type={"number"}
                            onChange={(value: string) =>
                                dispatch(
                                    setFormValues({
                                        ...formValues,
                                        telephoneNumber: value,
                                    }),
                                )
                            }
                            value={formValues?.telephoneNumber}
                            onInput={(event: FormEvent<HTMLInputElement>) => {
                                const target = event.target as HTMLInputElement;
                                if (target.value.length > 10) {
                                    target.value = target.value.slice(0, 10);
                                }
                            }}
                            title={"Telefoonnummer"}
                            optional={true}
                        />
                    </div>
                    <SubmitButtonComponent
                        isPatchEditEmployeeLoading={
                            isPatchEditEmployeeLoading || changeEmailIsLoading
                        }
                    />
                </form>
            )}
        </Popup>
    );
};
